import React, { useEffect } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";

const PaymentHistoryModal = React.forwardRef((props, ref) => {
  const consumedBy = props?.data?.consumedBy || [];

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" || event.keyCode === 27) props.onHide();
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [props]);

  const totalDebited = consumedBy.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const allocated = parseFloat(props?.data?.allocatedAmount || 0);
  const actualAmount = parseFloat(props?.data?.actualAmount || 0);
  const spentPercent = allocated > 0 ? ((actualAmount / allocated) * 100).toFixed(1) : 0;

  return (
    <Modal
      {...props}
      aria-labelledby="contained-modal-title-vcenter"
      keyboard={false}
      size="lg"
      ref={ref}
      centered
    >
      {/* Header */}
      <Modal.Header
        closeButton
        style={{
          background: "#92BCF2",
          borderBottom: "none",
          borderRadius: "8px 8px 0 0",
          padding: "16px 20px",
        }}
      >
        <Modal.Title
          id="contained-modal-title-vcenter"
          style={{ color: "#fff", width: "100%" }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                background: "rgba(255,255,255,0.2)",
                borderRadius: "8px",
                padding: "6px 8px",
                lineHeight: 1,
              }}
            >
              <i className="bi bi-clock-history" style={{ fontSize: "18px" }}></i>
            </div>
            <div>
              <div style={{ fontSize: "18px", fontWeight: 700 }}>Payment History</div>
              <div style={{ fontSize: "12px", opacity: 0.85, fontWeight: 400, marginTop: "2px" }}>
                Budget: <strong>{props?.data?.budgetNr}</strong> &nbsp;|&nbsp;
                {props?.data?.budgetDepartment || props?.data?.department} &nbsp;|&nbsp;
                {props?.data?.category}
              </div>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px", backgroundColor: "#f8faff" }}>

        {/* Summary Cards */}
        <div className="d-flex gap-2 mb-3">
          {[
            {
              label: "Allocated",
              value: `₹ ${parseFloat(props?.data?.allocatedAmount || 0).toLocaleString("en-IN")}`,
              icon: "bi bi-wallet2",
              color: "#2563eb",
              bg: "#eff6ff",
              border: "#bfdbfe",
            },
            {
              label: "Actual Spent",
              value: `₹ ${parseFloat(props?.data?.actualAmount || 0).toLocaleString("en-IN")}`,
              icon: "bi bi-arrow-up-circle",
              color: "#dc2626",
              bg: "#fff5f5",
              border: "#fecaca",
            },
            {
              label: "Remaining",
              value: `₹ ${parseFloat(props?.data?.remainingAmount || 0).toLocaleString("en-IN")}`,
              icon: "bi bi-piggy-bank",
              color: "#16a34a",
              bg: "#f0fdf4",
              border: "#bbf7d0",
            },
          ].map((card, i) => (
            <div
              key={i}
              className="flex-fill d-flex align-items-center gap-2 px-3 py-2 rounded-3"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <i className={card.icon} style={{ fontSize: "16px", color: card.color, flexShrink: 0 }}></i>
              <div>
                <div style={{ fontSize: "10px", color: "#6b7280", lineHeight: 1.2 }}>{card.label}</div>
                <div style={{ fontSize: "13px", fontWeight: 700, color: card.color, lineHeight: 1.4 }}>
                  {card.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Budget Utilization Bar */}
        <div className="mb-3 px-1">
          <div
            className="d-flex justify-content-between mb-1"
            style={{ fontSize: "12px", color: "#6b7280" }}
          >
            <span>Budget Utilization</span>
            <span
              style={{
                fontWeight: 600,
                color: spentPercent > 80 ? "#dc2626" : "#2563eb",
              }}
            >
              {spentPercent}% used
            </span>
          </div>
          <div
            style={{
              height: "7px",
              backgroundColor: "#dbeafe",
              borderRadius: "99px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.min(spentPercent, 100)}%`,
                background:
                  spentPercent > 80
                    ? "linear-gradient(90deg, #f97316, #dc2626)"
                    : "linear-gradient(90deg, #60a5fa, #2563eb)",
                borderRadius: "99px",
                transition: "width 0.6s ease",
              }}
            />
          </div>
        </div>

        {/* Invoice Table */}
        {consumedBy.length === 0 ? (
          <div
            className="text-center py-5 rounded-3"
            style={{ background: "#fff", border: "1px dashed #bfdbfe" }}
          >
            <i className="bi bi-inbox" style={{ fontSize: "36px", color: "#93c5fd" }}></i>
            <p className="mt-2 mb-0" style={{ fontSize: "14px", color: "#6b7280" }}>
              No invoices linked to this budget yet.
            </p>
          </div>
        ) : (
          <div
            className="rounded-3 overflow-hidden"
            style={{ border: "1px solid #dbeafe", boxShadow: "0 2px 8px rgba(37,99,235,0.07)" }}
          >
            <table className="table table-hover mb-0" style={{ fontSize: "13.5px" }}>
              <thead>
                <tr style={{ background: "linear-gradient(90deg, #eff6ff, #dbeafe)" }}>
                  {["#", "Invoice Nr", "Amount Debited", "Debited At"].map((h) => (
                    <th
                      key={h}
                      style={{
                        color: "#1e40af",
                        fontWeight: 600,
                        padding: "10px 14px",
                        border: "none",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#fff" }}>
                {consumedBy.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #eff6ff" }}>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{i + 1}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span
                        style={{
                          background: "#dbeafe",
                          color: "#1d4ed8",
                          padding: "3px 10px",
                          borderRadius: "99px",
                          fontWeight: 600,
                          fontSize: "12px",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {entry.invoiceNr}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ color: "#dc2626", fontWeight: 600 }}>
                        ₹ {parseFloat(entry.amount).toLocaleString("en-IN")}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#6b7280", fontSize: "12px" }}>
                      <i className="bi bi-calendar3 me-1"></i>
                      {new Date(entry.debitedAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#eff6ff" }}>
                  <td
                    colSpan={2}
                    className="text-end"
                    style={{ padding: "10px 14px", fontWeight: 700, color: "#1e40af" }}
                  >
                    Total Debited
                  </td>
                  <td
                    style={{ padding: "10px 14px", fontWeight: 700, color: "#dc2626", fontSize: "14px" }}
                  >
                    ₹ {totalDebited.toLocaleString("en-IN")}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Footer */}
        <Row className="justify-end mt-3">
          <Col xs={6} md={3} lg={3} className="commLeftRightPadding">
            <button
              onClick={() => props.onHide()}
              className="btn w-full mt-2 rounded-3 fw-semibold"
              style={{
                background: "linear-gradient(135deg, #4a90d9, #2563eb)",
                color: "#fff",
                border: "none",
                padding: "8px 0",
                boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
              }}
            >
              Close
            </button>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
});

export default PaymentHistoryModal;